import { ESCAPE } from '@angular/cdk/keycodes';
import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { inject, Injectable, Injector } from '@angular/core';
import { Subject } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { WizardOverlayConfig } from './model/wizard-overlay-config';
import { WizardControl, WizardDef } from './model/wizard.model';
import { WizardComponent } from './wizard.component';
import { DEFAULT_OVERLAY_CONFIGS, WIZARD_CONTROLS, WIZARD_DEF_TOKEN } from './wizard.constants';
/*
 * L2-R6: moved from `src/app/shared/wizard/wizard-manager.service.ts` (2 reports references, ~16
 * host ones). Its mutable fields (overlayRef, the _afterClosed Subject) are per-opened-overlay, so
 * the duplicated copy was a bundle-size problem rather than a state fork — but it dragged the whole
 * 10-file wizard closure into the remote's bundle.
 */
const makePositionStrategy = (overlay: Overlay) => overlay.position().global().centerHorizontally().top();
@Injectable({
  providedIn: 'root',
})
export class WizardManagerService {
  private overlayRef: OverlayRef;
  private readonly _afterClosed = new Subject();
  afterClosed$ = this._afterClosed.asObservable().pipe(take(1));
  public overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  /**
   * Creates a WizardComponent passing a wizard definition(or workflow) that contains a set of steps
   * components and parameters to load dynamically components what will accumulate information until
   * the last step. Every step component should implement either StepComponent<I,O> or StepSummary<I>
   *
   * @param wizard {WizardDef}
   * @param options {OverlayConfig}
   *
   * @returns Observable<{ afterClosed: Observable<DataObject>}>
   */
  public openWizard(wizard: WizardDef, options: OverlayConfig = { panelClass: 'filter-modals' }) {
    const wizardConfig = this.makeWizardConfig(wizard, options);
    this.overlayRef = this.overlay.create(wizardConfig);
    this.attachBackdropDisposal(this.overlayRef);
    this.attachKeyboardEscapeDisposal(this.overlayRef);
    this.createDialogContainer(this.overlayRef, wizardConfig);
    return { afterClosed: () => this.afterClosed$ };
  }
  private makeWizardConfig(wizard, options) {
    const positionStrategy = makePositionStrategy(this.overlay);
    const scrollStrategy = this.overlay.scrollStrategies.block();
    const defaultOptions = { ...DEFAULT_OVERLAY_CONFIGS, ...options };
    return new WizardOverlayConfig({ ...defaultOptions, wizard, positionStrategy, scrollStrategy });
  }
  private attachBackdropDisposal(overlayRef: OverlayRef) {
    overlayRef
      .backdropClick()
      .pipe(take(1))
      .subscribe(() => this.disposeBox());
  }
  /** Close the wizard when the user presses Escape. */
  private attachKeyboardEscapeDisposal(overlayRef: OverlayRef): void {
    overlayRef
      .keydownEvents()
      .pipe(
        filter(event => event.keyCode === ESCAPE),
        take(1)
      )
      .subscribe(() => this.disposeBox());
  }
  /**
   * Creates the ComponentPortal to be projected in cdk panel
   * @param overlayRef {OverlayRef}
   * @param config {WizardOverlayConfig}
   */
  private createDialogContainer(overlayRef: OverlayRef, config: WizardOverlayConfig) {
    const injector = this.makeEditingBoxInjectors(config);
    const portal = new ComponentPortal<WizardComponent>(WizardComponent, null, injector);
    overlayRef.attach(portal);
  }
  private makeEditingBoxInjectors({ wizardDef }: WizardOverlayConfig): Injector {
    return Injector.create({
      parent: this.injector,
      providers: [
        { provide: WIZARD_DEF_TOKEN, useValue: wizardDef },
        { provide: WIZARD_CONTROLS, useValue: this.makeWizardControl() },
      ],
    });
  }
  /**
   * Returns an object with a copy of this.disposeBox (remote control) to be passed
   * to the WizardComponent so it can close by itself. also sends out the captured
   * data in the wizard component if data is passed (similar to MatDialog.close()
   * OR MatDialog.close(data))
   */
  private makeWizardControl(): WizardControl {
    return { close: this.disposeBox.bind(this) };
  }
  disposeBox(data?: any) {
    // to send passed data out of wizard
    this._afterClosed.next(data);
    // destroy overlay
    if (this.overlayRef !== null) {
      this.overlayRef.detach();
      this.overlayRef.detachBackdrop();
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
